import React, { Fragment, useEffect } from 'react';
import { FocusEvent, useRef, useState } from 'react';
import clsx from 'clsx';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { v2filtersApiRequests } from 'utils/api-requests/v2filters';
import { ObjectAndSingleValueChangeType } from 'utils/types/InputTypes';
import { LocationItem, LocationOptionsType, SavedLocationsType, SearchLocationResultType } from 'utils/types/V2Locations';

import CheckboxTextInput from './CheckboxTextInput';
import TextInput from './TextInput';

const Container = styled.div`
    position: relative;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;
`;

const AutoCompleteSuggestions = styled.div`
    position: absolute;
    z-index: 1;
    top: calc(100% + 8px);
    display: flex;
    flex-direction: column;
    background: var(--color-white);
    left: 0;
    width: 100%;
    min-width: fit-content;
    max-height: 330px;
    overflow-y: auto;
    padding: 10px;
    gap: 6px;
    border-radius: 10px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-primary);

    &.hidden {
        display: none;
    }
`;

const Suggestion = styled.div`
    width: 100%;
    font-weight: 500;
    font-size: 13px;
    line-height: 36px;
    color: var(--primary-color);
    background: var(--color-alt-second);
    border-radius: 10px;
    text-align: center;
    border: 1px solid transparent;
    transition: border 0.1s ease-in-out;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;

    &.subSuggestion {
        margin-left: 20px;
        width: initial;
    }

    &.subSubSuggestion {
        margin-left: 40px;
        width: initial;
    }

    input {
        --checkbox-background-color: transparent;

        cursor: pointer;
    }

    input[type="checkbox"]:checked {
        --checkbox-color: var(--color-white);
    }

    &.active {
        background: ${(props) => props.darkMode ? 'var(--color-primary)' : 'var(--color-alt)'};
        color: var(--color-alt-second);

        div {
            color: var(--color-white);
        }

        img {
            filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%);
        }
    }
`;

const SelectAllDivider = styled.div`
    padding-bottom: 10px;
    padding-top: 10px;

    &::before {
        content: '';
        display: block;
        width: 100%;
        border-bottom: 1px solid rgba(3, 13, 69, 0.1);
    }
`;

const SelectAllSearchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 5px 12px 8px;
    cursor: pointer;
    border-bottom: 1px solid rgba(3, 13, 69, 0.1);
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-primary);
    letter-spacing: 0.3px;
    user-select: none;

    &:hover {
        opacity: 0.75;
    }
`;

const MiniCheckbox = styled.span<{ checked: boolean }>`
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border: 1.5px solid var(--color-primary);
    border-radius: 3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ checked }) => checked ? 'var(--color-primary)' : 'transparent'};
    transition: background 0.1s;

    &::after {
        content: '';
        display: ${({ checked }) => checked ? 'block' : 'none'};
        width: 3px;
        height: 6px;
        border: 1.5px solid white;
        border-top: none;
        border-left: none;
        transform: rotate(45deg) translateY(-1px);
    }
`;

const ErrorWrapper = styled.p`
    --font-size-body: var(--font-size-body-4);
    
    color: var(--color-error);
    padding: 0 10px;
    grid-column: 1 / -1;
`;

interface LocalizationAutocompleteInputProps {
    placeholder: string,
    onBlur?: (e: FocusEvent<HTMLInputElement | HTMLDivElement>) => void,
    options: LocationOptionsType[],
    searchResultsOptions: SearchLocationResultType[],
    onAction?: (voivodeship: number) => void,
    singleValue?: boolean,
    errorMessage?: string,
    inputValidation?: (value: string) => boolean,
    triggerValidation?: boolean,
    handleChange: (name: string, value: ObjectAndSingleValueChangeType) => void,
    savedLocations: SavedLocationsType,
    handleChangeSingleValueField?: (name: string, value: string) => void,
    cityName?: string,
    selectAllCheckbox?: boolean,
    savedLocationsFieldName: string;
    cityFieldName: string;
    hideTopLevelCheckboxes?: boolean;
}

const LocalizationAutocompleteInput = ({
    placeholder,
    onBlur,
    options,
    searchResultsOptions,
    onAction,
    singleValue,
    errorMessage,
    inputValidation,
    triggerValidation,
    handleChange,
    savedLocations,
    handleChangeSingleValueField,
    cityName,
    selectAllCheckbox,
    savedLocationsFieldName,
    cityFieldName,
    hideTopLevelCheckboxes
}: LocalizationAutocompleteInputProps) => {
    const [isSelectAll, setIsSelectAll] = useState(false);
    const minInputLength = 3;
    const [activeSuggestions, setActiveSuggestions] = useState<number[]>([]);
    const [activeCities, setActiveCities] = useState<number[]>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [inputValue, setInputValue] = useState<string>('');
    const { user } = useUser();
    // const { handleChange, handleChangeNewAdvertField, userFiltersState, newAdvertFields } = useContext(UserFiltersContext);
    const [isErrorInput, setIsErrorInput] = useState(false);

    const handleClick = () => {
        if (showSuggestions) return;

        setShowSuggestions(true);
    };

    const handleSelectSuggestion = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: LocationOptionsType, noAction?: boolean) => {
        e.preventDefault();
        e.stopPropagation();
        if (noAction) return;
        handleChangeLocation(item);

        if (singleValue) {
            setShowSuggestions(false);
        }
    };

    const handleChangeLocation = (item: LocationItem) => {
        const savedLocationsValue = singleValue ? {} as SavedLocationsType : savedLocations;
        setInputValue('');

        if (savedLocationsValue[item.id]) {
            delete savedLocationsValue[item.id];
        } else {
            savedLocationsValue[item.id] = item;
            setShowSuggestions(false);
        }

        handleChange(savedLocationsFieldName, savedLocationsValue);
        handleChange(cityFieldName, Object.values(savedLocationsValue));

        if (singleValue && handleChangeSingleValueField) {
            const firstLocation = Object.values(savedLocationsValue)[0];
            handleChangeSingleValueField('cityName', firstLocation?.label || '');
        }

        if (inputValidation) {
            const firstLocation = Object.values(savedLocationsValue)[0];
            setIsErrorInput(inputValidation(firstLocation?.label || ''));
        } else {
            setIsErrorInput(false);
        }
    };

    const handleOnAction = (voivodeshipLabel: string) => {
        const voivodeship = options.find((x) => x.label === voivodeshipLabel);
        if (!voivodeship) return;

        const voivodeshipId = voivodeship.id;

        if (activeSuggestions.includes(voivodeshipId)) {
            setActiveSuggestions(activeSuggestions.filter((x) => x !== voivodeshipId));
        } else {
            setActiveSuggestions([...activeSuggestions, voivodeshipId]);
        }

        onAction?.(voivodeshipId);
    };

    const areAllSearchResultsSelected = searchResultsOptions?.length > 0 &&
        searchResultsOptions.every((x) => !!savedLocations[x.id]);

    const handleSelectAllSearchResults = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const newSavedLocations = { ...savedLocations };

        if (areAllSearchResultsSelected) {
            searchResultsOptions.forEach((x) => {
                delete newSavedLocations[x.id];
            });
        } else {
            searchResultsOptions.forEach((x) => {
                newSavedLocations[x.id] = x;
            });
        }

        handleChange(savedLocationsFieldName, newSavedLocations);
        handleChange(cityFieldName, Object.values(newSavedLocations));
    };

    const handleClickCity = (cityLabel: string) => {
        let cityId: number | undefined;

        for (const voivodeship of options) {
            if (activeSuggestions.includes(voivodeship.id)) {
                const city = voivodeship.children.find((c) => c.label === cityLabel);

                if (city) {
                    cityId = city.id;
                    break;
                }
            }
        }

        if (cityId === undefined) return;

        if (activeCities.includes(cityId)) {
            setActiveCities(activeCities.filter((x) => x !== cityId));
        } else {
            setActiveCities([...activeCities, cityId]);
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLDivElement | HTMLTextAreaElement>) => {
        if (onBlur) onBlur(e as FocusEvent<HTMLInputElement | HTMLDivElement>);

        if (inputValidation && !showSuggestions) {
            setIsErrorInput(inputValidation(cityName || ''));
        } else {
            setIsErrorInput(false);
        }
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (inputValue.length >= minInputLength) {
            timer = setTimeout(async () => {
                const searchResults = await v2filtersApiRequests.getLocationsFromSearch(inputValue);

                handleChange('searchLocationResult', searchResults);
            }, 1000);
        }

        if (inputValue.length === 0) {
            handleChange('searchLocationResult', []);
        }

        return () => clearTimeout(timer);
    }, [inputValue]);

    useEffect(() => {
        if (suggestionsRef.current && inputValue.length >= minInputLength) {
            suggestionsRef.current.scrollTop = 0;
        }
    }, [inputValue, searchResultsOptions]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (inputValue.length >= minInputLength) {
            timer = setTimeout(async () => {
                const searchResults = await v2filtersApiRequests.getLocationsFromSearch(inputValue);

                handleChange('searchOfficeLocationResult', searchResults);
            }, 1000);
        }

        if (inputValue.length === 0) {
            handleChange('searchOfficeLocationResult', []);
        }

        return () => clearTimeout(timer);
    }, [inputValue]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) && inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
                setActiveSuggestions([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [suggestionsRef, inputRef]);

    useEffect(() => {
        if (triggerValidation && inputValidation) {
            setIsErrorInput(inputValidation(cityName || ''));
        }
    }, [triggerValidation]);

    useEffect(() => {
        if (isSelectAll) {
            options.forEach((x) => {
                if (!savedLocations[x.id]) {
                    handleChangeLocation(x);
                }
            });
        } else {
            handleChange(savedLocationsFieldName, {});
            handleChange(cityFieldName, []);
        }
    }, [isSelectAll]);

    return (
        <>
            <Container>
                <TextInput
                    ref={inputRef}
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                    }}
                    onClick={() => {
                        inputRef.current?.focus();
                        handleClick();
                    }}
                    onBlur={handleBlur}
                    isError={isErrorInput}
                />
                <AutoCompleteSuggestions className={clsx({
                    hidden: !(showSuggestions && options.length > 0)
                })} ref={suggestionsRef}>
                    {selectAllCheckbox
                        ? <>
                            <Suggestion
                                darkMode={user.isDarkMode}
                                onClick={() => setIsSelectAll(!isSelectAll)}
                                className={clsx({ active: isSelectAll })}
                            >
                                <CheckboxTextInput
                                    label={'Wszystkie województwa'}
                                    value={isSelectAll}
                                />
                            </Suggestion>
                            <SelectAllDivider />
                        </>
                        : null}

                    {Object.values(savedLocations)
                        .filter((x: LocationItem) => !searchResultsOptions?.some((s) => s.id === x.id))
                        .map((x: LocationItem) => {
                            return (
                                <Suggestion
                                    darkMode={user.isDarkMode}
                                    key={x.id}
                                    onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleSelectSuggestion(e, x)}
                                >
                                    <CheckboxTextInput
                                        label={x.label}
                                        value={!!savedLocations[x.id]}
                                    />
                                </Suggestion>
                            );
                        })}
                    {searchResultsOptions?.length > 0
                        ? <>
                            {!singleValue &&
                                <SelectAllSearchRow onClick={handleSelectAllSearchResults}>
                                    {areAllSearchResultsSelected
                                        ? 'Odznacz wszystkie'
                                        : `Zaznacz wszystkie (${searchResultsOptions.length})`}
                                    <MiniCheckbox checked={areAllSearchResultsSelected} />
                                </SelectAllSearchRow>
                            }
                            {searchResultsOptions.map((x) => <Suggestion
                                darkMode={user.isDarkMode}
                                key={x.id}
                                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleSelectSuggestion(e, x)}
                                className={clsx({ active: savedLocations[x.id] })}
                            >
                                <CheckboxTextInput
                                    label={x.label}
                                    value={!!savedLocations[x.id]}
                                />
                            </Suggestion>)}
                        </>
                        : options.map((x) => <Fragment key={x.id}>
                            <Suggestion
                                darkMode={user.isDarkMode}
                                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleSelectSuggestion(e, x, hideTopLevelCheckboxes ? true : false)}
                                className={clsx({ active: savedLocations[x.id] })}
                            >
                                <CheckboxTextInput
                                    label={x.label}
                                    value={!!savedLocations[x.id]}
                                    onAction={handleOnAction}
                                    hideTopLevelCheckboxes={hideTopLevelCheckboxes}
                                />
                            </Suggestion>

                            {x.children && x.children.length > 0 && activeSuggestions.includes(x.id)
                                ? x.children.map((city: LocationItem) => <Fragment key={city.id}>
                                    <Suggestion
                                        darkMode={user.isDarkMode}
                                        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleSelectSuggestion(e, city)}
                                        className={clsx('subSuggestion', { active: savedLocations[city.id] })}
                                    >
                                        <CheckboxTextInput
                                            label={city.label}
                                            value={!!savedLocations[city.id]}
                                            onAction={city.children.length > 0 ? handleClickCity : undefined}
                                        />
                                    </Suggestion>
                                    {city.children && city.children.length > 0 && activeCities.includes(city.id)
                                        ? city.children.map((district) => <Suggestion
                                            darkMode={user.isDarkMode}
                                            key={district.id}
                                            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleSelectSuggestion(e, district)}
                                            className={clsx('subSubSuggestion', { active: savedLocations[district.id] })}
                                        >
                                            <CheckboxTextInput
                                                label={district.label}
                                                value={!!savedLocations[district.id]}
                                            />
                                        </Suggestion>)
                                        : null}
                                </Fragment>)
                                : null}
                        </Fragment>)}
                </AutoCompleteSuggestions>
            </Container>
            {isErrorInput ? <ErrorWrapper>{errorMessage}</ErrorWrapper> : null}
        </>
    );
};

export default LocalizationAutocompleteInput;
